import React, { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ordersApi } from "../services/orders";
import { formatTxnAmount } from "../utils/currency";
import "./SetlistUploadFlow.css";
import "./CashoutProtectModal.css";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatBytes } from "../utils/formatBytes";
import { classifyUploadExtension } from "../utils/uploadFileKind";
import { publicUrl } from "../utils/publicUrl";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Post-cashout "Protect Your Cashout" prompt — rebuilt on the
// Upload Track History panel (SetlistUploadFlow, founder-approved) so
// the two upload screens are ONE design: title + ✕, the dashed dropzone, "Upload from laptop", a short
// helper, ghost + money-forward primary. The classes are imported from the
// reference stylesheet on purpose — a polish to one is a polish to both.
//
// No celebration line here on purpose (founder decision): this screen's job is
// to URGE the upload, not to congratulate — title, dropzone and "Protect $X"
// carry it; the helper holds the one-sentence why.
//
// Fires after a successful /payments/cashout when at least one currency
// bucket in the cashout >= 100 in its own unit (no FX — $100, €100, £100 each
// trigger independently). One step: drop the file, tap "Protect $X". POSTs
// /orders/upload-history with the paid-out orderIds; success shows the
// covered state. Also reopened by /requests?protect=1 (the laptop link) for
// the most recent payout — see RequestsPage.

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8", ".png", ".jpg", ".jpeg"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
// Wall mode (first-cashout wall, fraud door) accepts EXPORTS only —
// the server validates the file against the songs automatically and a
// screenshot can't be checked.
const EXPORT_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8"];
const EXPORT_ACCEPT_ATTR = EXPORT_EXTENSIONS.join(",");
const MAX_BYTES = 25 * 1024 * 1024;

interface CurrencyTotal {
  currency: string;
  amount: number;
}

interface Props {
  orderIds: string[];
  totals: CurrencyTotal[]; // one entry per currency bucket; UI joins them
  onClose: () => void;
  // "protect" (default): the post-cashout prompt. "wall": the server answered
  // the cashout with 409 first_cashout_wall — the upload is REQUIRED (export
  // only, validated server-side with strict=1) and onProtected re-fires the
  // cashout once the file is in.
  mode?: "protect" | "wall";
  onProtected?: () => void;
}

const CashoutProtectModal: React.FC<Props> = ({ orderIds, totals, onClose, mode = "protect", onProtected }) => {
  const isWall = mode === "wall";
  const [phase, setPhase] = useState<"pick" | "success">("pick");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // "$140" / "$140 + €40" — the same formatter the reference panel uses, one
  // entry per currency, never blended.
  const moneyLabel = useMemo(
    () => totals.map((t) => formatTxnAmount(t.amount, t.currency)).join(" + "),
    [totals]
  );
  const orderCount = orderIds.length;
  const fileKind = file ? classifyUploadExtension(file.name) : null;

  const handleFiles = useCallback((list: FileList | File[] | null) => {
    setError(null);
    if (!list || list.length === 0) return;
    const first = list[0];
    if (!classifyUploadExtension(first.name)) {
      setError(tr("cashoutProtectModal.thatFileTypeIsn", { value: (isWall ? EXPORT_EXTENSIONS : ACCEPTED_EXTENSIONS).join(", ") }));
      return;
    }
    if (isWall && classifyUploadExtension(first.name) === "screenshot") {
      setError(
        tr("cashoutProtectModal.forYourFirstCashout", { EXPORT_EXTENSIONS: EXPORT_EXTENSIONS.join(", ") })
      );
      return;
    }
    if (first.size > MAX_BYTES) {
      setError(tr("cashoutProtectModal.fileIsTooLarge", { formatBytes: formatBytes(first.size) }));
      return;
    }
    setFile(first);
  }, [isWall]);

  // Phone → laptop hand-off (the export file lives on the computer). The link
  // reopens THIS modal on the laptop for the most recent payout
  // (/requests?protect=1 — RequestsPage rebuilds the order list from it).
  const uploadLink = publicUrl("/requests?protect=1");
  const copyUploadLink = () => {
    copyTextToClipboard(uploadLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const submit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    setError(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("orderIds", JSON.stringify(orderIds));
    if (isWall) fd.append("strict", "1"); // server refuses a file that names none of the songs
    try {
      const data = await ordersApi.uploadHistory(fd);
      setUploadedFilename(data?.filename || file.name);
      setPhase("success");
      // Wall: the file is in — let "Unlocked." land, then re-fire the cashout.
      if (isWall && onProtected) setTimeout(onProtected, 900);
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { msg?: string } } };
      const status = e?.response?.status;
      const msg = e?.response?.data?.msg;
      if (status === 413) setError(msg || tr("cashoutProtectModal.fileTooLarge"));
      else if (status === 422) setError(msg || tr("cashoutProtectModal.weCouldnTFind"));
      else if (status === 415) setError(msg || tr("cashoutProtectModal.fileTypeNotAccepted"));
      else if (status === 409) setError(msg || tr("cashoutProtectModal.theseOrdersCanT"));
      else if (status === 403) setError(msg || tr("cashoutProtectModal.youDonTOwn"));
      else if (status === 404) setError(msg || tr("cashoutProtectModal.couldnTFindThe"));
      else if (status === 502) setError(msg || tr("cashoutProtectModal.weCouldnTStore"));
      else setError(tr("cashoutProtectModal.uploadFailedPleaseTry"));
    } finally {
      setSubmitting(false);
    }
  };

  const head = (
    <div className="suf-head">
      <div className="suf-title">{isWall ? tr("cashoutProtectModal.unlockYourFirstCashout") : tr("cashoutProtectModal.protectYourCashout")}</div>
      <button type="button" className="suf-close" onClick={onClose} aria-label={tr("cashoutProtectModal.close")}>
        ✕
      </button>
    </div>
  );

  const node = (
    <div className="suf-overlay" onClick={submitting ? undefined : onClose}>
      <div className="suf-panel" onClick={(e) => e.stopPropagation()}>
        {phase === "pick" && (
          <>
            {head}
            <div
              className={`suf-drop${dragOver ? " is-drag" : ""}${file ? " suf-drop--captured" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer?.files || null);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={isWall ? EXPORT_ACCEPT_ATTR : ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {file ? (
                <div className="suf-drop-file">
                  <div className="suf-file-check" aria-hidden>✓</div>
                  <div className="suf-file-captured-title">{tr("cashoutProtectModal.fileCaptured")}</div>
                  <div className="suf-drop-filename">{file.name}</div>
                  <div className="suf-drop-filemeta">
                    {formatBytes(file.size)}
                    {fileKind === "screenshot" ? tr("cashoutProtectModal.screenshotAnExportIs") : tr("cashoutProtectModal.readyToProtect")}
                  </div>
                </div>
              ) : (
                <>
                  <svg className="suf-drop-icon" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#24C961" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 16V4M7 9l5-5 5 5" />
                    <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
                  </svg>
                  <div className="suf-drop-title">{tr("cashoutProtectModal.uploadTrackHistory")}</div>
                  <div className="suf-drop-sub">{tr("cashoutProtectModal.tapToBrowse")}</div>
                </>
              )}
            </div>

            {isWall ? (
              <div className="suf-laptop-link" aria-live="polite">
                <span className="suf-laptop-link-text">
                  {tr("cashoutProtectModal.exportLivesOnYour")}
                </span>
              </div>
            ) : (
              <div className="suf-laptop-link" aria-live="polite">
                <span className="suf-laptop-link-text">{tr("cashoutProtectModal.uploadFromLaptop")}</span>
                <button type="button" className="suf-laptop-link-btn" onClick={copyUploadLink}>
                  {linkCopied ? tr("cashoutProtectModal.copied") : tr("cashoutProtectModal.copyLink")}
                </button>
              </div>
            )}

            <p className="suf-helper">
              {isWall ? (
                <>
                  <T k="cashoutProtectModal.firstCashoutNeedsHistory" vars={{ moneyLabel, count: orderCount }} tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong> }} />
                </>
              ) : (
                <>
                  <T k="cashoutProtectModal.ifAFanDisputes" vars={{ count: orderCount }} tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong> }} />
                </>
              )}
            </p>

            {error && <div className="suf-error">{error}</div>}

            <div className="suf-actions">
              <button type="button" className="suf-btn-ghost" onClick={onClose} disabled={submitting}>
                {isWall ? tr("cashoutProtectModal.notNow") : tr("cashoutProtectModal.skip")}
              </button>
              <button type="button" className="suf-btn-go" disabled={!file || submitting} onClick={submit}>
                {submitting ? (isWall ? tr("cashoutProtectModal.checking") : tr("cashoutProtectModal.uploading")) : isWall ? tr("cashoutProtectModal.unlock", { moneyLabel }) : tr("cashoutProtectModal.protect", { moneyLabel })}
              </button>
            </div>
          </>
        )}

        {phase === "success" && (
          <>
            {head}
            <div className="suf-done">
              <div className="suf-done-check" aria-hidden>✓</div>
              <div className="suf-done-title">{isWall ? tr("cashoutProtectModal.unlocked") : tr("cashoutProtectModal.youReCovered")}</div>
              <div className="suf-done-sub cpm-done-sub">
                {isWall ? (
                  <>
                    <T k="cashoutProtectModal.fileAttachedCashingOut" vars={{ file: uploadedFilename ?? "", count: orderCount }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                  </>
                ) : (
                  <>
                    <T k="cashoutProtectModal.fileAttachedAutoEvidence" vars={{ file: uploadedFilename ?? "", count: orderCount }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                  </>
                )}
              </div>
              <button type="button" className="suf-btn-go suf-btn-done" onClick={isWall ? onProtected || onClose : onClose}>
                {isWall ? tr("cashoutProtectModal.cashOut") : tr("cashoutProtectModal.done")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default CashoutProtectModal;
